/**
 * useVOECall Composable
 *
 * Manages VOE-specific call state that wraps around the phone store.
 * Handles: VOE mode toggling, call script data, verification form,
 * contact attempt logging, and voicemail drop.
 */
import { ref, computed, watch } from 'vue'
import { usePhoneStore } from '../stores/phone'
import { useAuthStore } from '../stores/auth'
import { voeService } from '../services/voe.service'
import { twilioService } from '../services/twilio.service'
import type { VOECallContext, PhoneAttemptOutcome } from '../types/voe'
import { useToast } from 'primevue/usetoast'

// Singleton state — shared across components
const isVOEMode = ref(false)
const mode = ref<'call' | 'log'>('call')
const employerId = ref<string | null>(null)
const context = ref<VOECallContext | null>(null)
const loading = ref(false)

// Form state
const outcome = ref<PhoneAttemptOutcome | null>(null)
const verification = ref({
  respondentName: '',
  respondentTitle: '',
  datesConfirmed: null as boolean | 'unable' | null,
  employmentStart: '',
  employmentEnd: '',
  positionHeld: '',
  positionConfirmed: null as boolean | 'unable' | null,
  reasonForLeaving: '',
  subjectToFmcsa: null as boolean | null,
  hasAccidents: null as boolean | null,
  accidents: [] as string[],
  hasViolations: null as boolean | null,
  violations: [] as string[],
  eligibleForRehire: null as boolean | null,
  subjectToDrugAlcoholTesting: null as boolean | null,
  hasDrugAlcoholViolation: null as boolean | null,
  drugAlcoholViolationDetails: '',
  completedSapProgram: null as boolean | null,
  wasDisqualified: null as boolean | null,
  workRecordNotes: '',
})
const notes = ref('')
const referralContact = ref({
  name: '',
  title: '',
  phone: '',
  email: '',
})
const scriptCollapsed = ref(false)
const isSaving = ref(false)
const isDroppingVM = ref(false)
const isSendingForm = ref(false)

// Callback for "Send Form" — VOEView registers openEmailPreview here
let _onSendForm: ((employerId: string) => void) | null = null

// Callback for after save — VOEView registers refreshRecord here
let _onSaveComplete: ((recordId: string) => void) | null = null

// Desk phone state
const deskPhoneCallSid = ref<string | null>(null)
const deskPhoneStatus = ref<'idle' | 'ringing' | 'in-progress' | 'completed' | 'busy' | 'failed' | 'no-answer' | 'canceled'>('idle')
const deskPhoneDuration = ref(0)
let _deskPhonePoller: ReturnType<typeof setInterval> | null = null
let _deskPhoneTimer: ReturnType<typeof setInterval> | null = null

export function useVOECall() {
  const phone = usePhoneStore()
  const toast = useToast()

  // ─── Computed ─────────────────────────────────────────────

  const canSave = computed(() => outcome.value !== null)

  const canDial = computed(
    () => mode.value === 'call' && !phone.isOnCall && phone.isReady && context.value?.employer.phone !== null
  )

  const canDropVoicemail = computed(
    () => mode.value === 'call' && phone.isOnCall && !isDroppingVM.value
  )

  const isDeskPhoneCall = computed(() => deskPhoneCallSid.value !== null)

  const isDeskPhoneActive = computed(
    () => deskPhoneStatus.value === 'ringing' || deskPhoneStatus.value === 'in-progress'
  )

  const canDialDeskPhone = computed(
    () => mode.value === 'call' && !phone.isOnCall && !isDeskPhoneActive.value && context.value?.employer.phone !== null
  )

  const canSendForm = computed(
    () => context.value?.employer.email !== null && !isSendingForm.value
  )

  const callerFirstName = computed(() => {
    const auth = useAuthStore()
    return auth.user?.first_name || 'a representative'
  })

  const callScript = computed(() => {
    if (!context.value) return ''
    const { record } = context.value
    const clientName = record.client_name || 'your company'
    return (
      `Hello, this is ${callerFirstName.value} with ClaimShield. ` +
      `We provide safety and compliance for ${clientName} ` +
      `and the reason for my call is to complete a verification of employment ` +
      `for ${record.employee_name}.`
    )
  })

  const attemptLabel = computed(() => {
    if (!context.value) return ''
    const { count, max } = context.value.attempts
    return `Attempt ${count + 1} of ${max}`
  })

  const formattedPhone = computed(() => {
    const raw = context.value?.employer.phone
    if (!raw) return ''
    const digits = raw.replace(/\D/g, '')
    if (digits.length === 11 && digits[0] === '1') {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    }
    return raw
  })

  // ─── Actions ──────────────────────────────────────────────

  /** Start a VOE call: fetch context, enter VOE mode, optionally dial */
  async function startVOECall(empId: string, callMode: 'call' | 'log') {
    loading.value = true
    try {
      const data = await voeService.getCallContext(empId)
      context.value = data
      employerId.value = empId
      mode.value = callMode
      isVOEMode.value = true

      // Reset form, pre-populate dates from employer data
      outcome.value = null
      verification.value = {
        respondentName: '',
        respondentTitle: '',
        datesConfirmed: null,
        employmentStart: data.employer.employment_start || '',
        employmentEnd: data.employer.employment_end || '',
        positionHeld: data.employer.position_held || '',
        positionConfirmed: null,
        reasonForLeaving: '',
        subjectToFmcsa: data.employer.subject_to_fmcsa,
        hasAccidents: null,
        accidents: [],
        hasViolations: null,
        violations: [],
        eligibleForRehire: null,
        subjectToDrugAlcoholTesting: null,
        hasDrugAlcoholViolation: null,
        drugAlcoholViolationDetails: '',
        completedSapProgram: null,
        wasDisqualified: null,
        workRecordNotes: '',
      }
      notes.value = ''
      referralContact.value = { name: '', title: '', phone: '', email: '' }
      scriptCollapsed.value = false

      // Expand the phone widget to show VOE panel (don't auto-dial)
      phone.isExpanded = true
    } catch (err: any) {
      toast.add({
        severity: 'error',
        summary: 'Failed to load call context',
        detail: err.message,
        life: 5000,
      })
    } finally {
      loading.value = false
    }
  }

  /** Dial the employer phone number */
  async function dial() {
    if (!context.value?.employer.phone || phone.isOnCall) return
    await phone.makeCall(context.value.employer.phone)
  }

  /** Initiate a desk phone bridge call to the employer */
  async function dialDeskPhone() {
    if (!context.value?.employer.phone) return

    deskPhoneStatus.value = 'ringing'
    try {
      const result = await twilioService.deskPhoneCall(context.value.employer.phone)
      deskPhoneCallSid.value = result.call_sid

      // Start polling call status every 2 seconds
      _deskPhonePoller = setInterval(async () => {
        if (!deskPhoneCallSid.value) return
        try {
          const status = await twilioService.deskPhoneStatus(deskPhoneCallSid.value)
          deskPhoneStatus.value = status.status as typeof deskPhoneStatus.value

          // Start duration timer when call connects
          if (status.status === 'in-progress' && !_deskPhoneTimer) {
            deskPhoneDuration.value = 0
            _deskPhoneTimer = setInterval(() => {
              deskPhoneDuration.value++
            }, 1000)
          }

          // Stop polling when call ends
          if (['completed', 'busy', 'failed', 'no-answer', 'canceled'].includes(status.status)) {
            stopDeskPhonePolling()
          }
        } catch {
          // Polling error — don't crash, just skip this tick
        }
      }, 2000)
    } catch (err: any) {
      deskPhoneStatus.value = 'failed'
      toast.add({
        severity: 'error',
        summary: 'Desk phone call failed',
        detail: err?.response?.data?.detail || err.message,
        life: 5000,
      })
    }
  }

  /** Hang up the desk phone bridge call */
  async function hangupDeskPhone() {
    if (!deskPhoneCallSid.value) return
    try {
      await twilioService.deskPhoneHangup(deskPhoneCallSid.value)
      deskPhoneStatus.value = 'completed'
      stopDeskPhonePolling()
    } catch (err: any) {
      toast.add({
        severity: 'error',
        summary: 'Hangup failed',
        detail: err?.response?.data?.detail || err.message,
        life: 5000,
      })
    }
  }

  /** Stop polling and timers */
  function stopDeskPhonePolling() {
    if (_deskPhonePoller) {
      clearInterval(_deskPhonePoller)
      _deskPhonePoller = null
    }
    if (_deskPhoneTimer) {
      clearInterval(_deskPhoneTimer)
      _deskPhoneTimer = null
    }
  }

  /** Drop voicemail into active call */
  async function dropVoicemail() {
    const callSid = phone.activeCallSid
    if (!callSid) {
      toast.add({ severity: 'warn', summary: 'No active call SID', life: 3000 })
      return
    }

    isDroppingVM.value = true
    try {
      await twilioService.voeVoicemailDrop(callSid)
      outcome.value = 'voicemail'
      toast.add({ severity: 'success', summary: 'Voicemail dropped', life: 3000 })
    } catch (err: any) {
      toast.add({
        severity: 'error',
        summary: 'Voicemail drop failed',
        detail: err.message,
        life: 5000,
      })
    } finally {
      isDroppingVM.value = false
    }
  }

  /** Open email preview dialog for this employer */
  function sendFormLink() {
    if (!employerId.value) return
    if (_onSendForm) {
      _onSendForm(employerId.value)
    }
  }

  /** Register a callback for when "Send Form" is clicked (used by VOEView) */
  function registerSendFormHandler(handler: (employerId: string) => void) {
    _onSendForm = handler
  }

  /** Register a callback for after save completes (used by VOEView to refresh) */
  function registerSaveHandler(handler: (recordId: string) => void) {
    _onSaveComplete = handler
  }

  /** Save contact attempt + optional verification, then exit VOE mode */
  async function saveAndClose() {
    if (!employerId.value || !outcome.value) return

    isSaving.value = true
    try {
      // "Got referral" — update contact info only, no attempt logged
      if (outcome.value === 'got_referral') {
        const ref = referralContact.value
        const updates: Record<string, unknown> = {}
        if (ref.name) updates.contact_name = ref.name
        if (ref.title) updates.contact_title = ref.title
        if (ref.phone) updates.phone = ref.phone
        if (ref.email) updates.email = ref.email
        if (Object.keys(updates).length > 0) {
          await voeService.updateEmployer(employerId.value, updates)
        }
        toast.add({
          severity: 'info',
          summary: 'Contact info updated',
          detail: `${context.value?.employer.name} — new contact saved${notes.value ? '. Notes: ' + notes.value : ''}`,
          life: 5000,
        })

        const recordId = context.value?.record?.id
        exitVOEMode()
        phone.isExpanded = false
        if (recordId && _onSaveComplete) {
          _onSaveComplete(recordId)
        }
        return
      }

      // Map UI outcomes to API outcomes for the attempt log
      const outcomeMap: Record<string, string> = {
        validated: 'verified',
        did_not_work_here: 'unable_to_verify',
      }
      const logOutcome = outcomeMap[outcome.value] || outcome.value

      // Build notes based on outcome
      let logNotes = notes.value || undefined
      if (outcome.value === 'did_not_work_here') {
        logNotes = `Employer states driver did not work there.${notes.value ? ' ' + notes.value : ''}`
      }

      // 1. Log the contact attempt
      await voeService.logAttempt(employerId.value, {
        method: 'phone',
        outcome: logOutcome,
        contact_used: context.value?.employer.phone || undefined,
        call_duration_seconds: mode.value === 'call'
          ? (isDeskPhoneCall.value ? deskPhoneDuration.value : phone.callDuration)
          : undefined,
        notes: logNotes,
      })

      // 2a. "Did not work here" — attempt logged with unable_to_verify outcome above; no separate call needed
      if (outcome.value === 'did_not_work_here') {
        toast.add({
          severity: 'warn',
          summary: 'Did Not Work Here',
          detail: `${context.value?.employer.name} — employer denied employment`,
          life: 5000,
        })
      }
      // 2b. "Validated" — verify the employer with all FMCSA data
      else if (outcome.value === 'validated' && !verification.value.respondentName.trim()) {
        toast.add({
          severity: 'error',
          summary: 'Respondent name required',
          detail: 'Enter the name of who you spoke with before saving.',
          life: 5000,
        })
        isSaving.value = false
        return
      }
      else if (outcome.value === 'validated' && verification.value.respondentName) {
        const v = verification.value
        // Convert "unable" to undefined (employer couldn't verify that field)
        const toBool = (val: any): boolean | undefined => val === 'unable' || val === null ? undefined : val
        await voeService.verifyEmployer(employerId.value, {
          verification_method: 'phone',
          respondent_name: v.respondentName,
          respondent_title: v.respondentTitle || undefined,
          employment_start: v.datesConfirmed === 'unable' ? undefined : (v.employmentStart || undefined),
          employment_end: v.datesConfirmed === 'unable' ? undefined : (v.employmentEnd || undefined),
          position_held: v.positionConfirmed === 'unable' ? undefined : (v.positionHeld || undefined),
          reason_for_leaving: v.reasonForLeaving || undefined,
          subject_to_fmcsa: toBool(v.subjectToFmcsa),
          has_accidents: toBool(v.hasAccidents),
          accident_details: v.hasAccidents === true ? (v.accidents.filter(Boolean).join('\n') || undefined) : undefined,
          has_violations: toBool(v.hasViolations),
          violation_details: v.hasViolations === true ? (v.violations.filter(Boolean).join('\n') || undefined) : undefined,
          eligible_for_rehire: toBool(v.eligibleForRehire),
          subject_to_drug_alcohol_testing: toBool(v.subjectToDrugAlcoholTesting),
          has_drug_alcohol_violation: toBool(v.hasDrugAlcoholViolation),
          drug_alcohol_violation_details: v.hasDrugAlcoholViolation === true ? (v.drugAlcoholViolationDetails || undefined) : undefined,
          completed_sap_program: toBool(v.completedSapProgram),
          was_disqualified: toBool(v.wasDisqualified),
          work_record_notes: v.workRecordNotes || undefined,
        })
        toast.add({
          severity: 'success',
          summary: 'Employer verified!',
          detail: `${context.value?.employer.name} marked as verified`,
          life: 5000,
        })
      } else {
        toast.add({
          severity: 'info',
          summary: 'Contact attempt logged',
          detail: `${logOutcome.replace(/_/g, ' ')} recorded for ${context.value?.employer.name}`,
          life: 4000,
        })
      }

      // 3. Notify parent to refresh, then exit VOE mode and collapse
      const recordId = context.value?.record?.id
      exitVOEMode()
      phone.isExpanded = false
      if (recordId && _onSaveComplete) {
        _onSaveComplete(recordId)
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err.message
      toast.add({
        severity: 'error',
        summary: 'Save failed',
        detail,
        life: 5000,
      })
    } finally {
      isSaving.value = false
    }
  }

  /** Exit VOE mode without saving */
  function exitVOEMode() {
    isVOEMode.value = false
    mode.value = 'call'
    employerId.value = null
    context.value = null
    outcome.value = null
    notes.value = ''
    scriptCollapsed.value = false

    // Desk phone cleanup — hang up if still active
    if (deskPhoneCallSid.value && (deskPhoneStatus.value === 'ringing' || deskPhoneStatus.value === 'in-progress')) {
      twilioService.deskPhoneHangup(deskPhoneCallSid.value).catch(() => {})
    }
    stopDeskPhonePolling()
    deskPhoneCallSid.value = null
    deskPhoneStatus.value = 'idle'
    deskPhoneDuration.value = 0
  }

  // If the call disconnects while in VOE call mode, keep the UI open
  // (don't auto-close — let them fill in outcome and save)
  watch(() => phone.isOnCall, (onCall, wasOnCall) => {
    if (wasOnCall && !onCall && isVOEMode.value && mode.value === 'call') {
      phone.isExpanded = true
    }
  })

  return {
    // State
    isVOEMode,
    mode,
    context,
    loading,
    outcome,
    verification,
    notes,
    referralContact,
    scriptCollapsed,
    isSaving,
    isDroppingVM,
    isSendingForm,

    // Computed
    canSave,
    canDial,
    canDropVoicemail,
    canSendForm,
    callScript,
    attemptLabel,
    formattedPhone,

    // Desk phone
    deskPhoneStatus,
    deskPhoneDuration,
    isDeskPhoneCall,
    isDeskPhoneActive,
    canDialDeskPhone,
    dialDeskPhone,
    hangupDeskPhone,

    // Actions
    startVOECall,
    dial,
    dropVoicemail,
    sendFormLink,
    registerSendFormHandler,
    registerSaveHandler,
    saveAndClose,
    exitVOEMode,
  }
}
